import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import getAssetImageUrl from '../utils/assetImageUrl';

// Cache for failed image URLs to avoid repeated 404 requests
const failedImageCache = new Set();

// Simple loader to inject the Google Maps JS API once
let googleMapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const keyParam = apiKey ? `&key=${apiKey}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=marker,places${keyParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

// Zones that should have assets randomly scattered within their polygons
// Note: Warehouses (with GPS polygons) are handled separately
const ZONES_WITH_RANDOM_SCATTER = ['zone-a', 'zone-b', 'zone-c'];

// Point-in-polygon check using ray casting algorithm
function isPointInPolygon(point, polygon) {
  const { lat, lng } = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    const intersect = ((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

// Simple deterministic hash function to convert a string to a number between 0 and 1
function hashStringToNumber(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to a number between 0 and 1
  return Math.abs(hash) / 2147483647;
}

// Seeded random number generator for deterministic positioning
function seededRandom(seed) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

// Generate a deterministic point within a polygon based on asset ID
// This ensures the same asset always appears at the same position
function getDeterministicPointInPolygon(polygon, assetId) {
  // Find bounding box
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;
  
  polygon.forEach(coord => {
    minLat = Math.min(minLat, coord.lat);
    maxLat = Math.max(maxLat, coord.lat);
    minLng = Math.min(minLng, coord.lng);
    maxLng = Math.max(maxLng, coord.lng);
  });
  
  // Use asset ID to generate deterministic "random" values
  const baseSeed = hashStringToNumber(assetId);
  
  // Generate deterministic points until one is inside the polygon
  let attempts = 0;
  while (attempts < 100) {
    // Use different seeds for lat and lng, combining base seed with attempt number
    const latSeed = seededRandom(baseSeed + attempts * 0.1);
    const lngSeed = seededRandom(baseSeed + attempts * 0.1 + 0.5);
    
    const lat = minLat + latSeed * (maxLat - minLat);
    const lng = minLng + lngSeed * (maxLng - minLng);
    const point = { lat, lng };
    
    if (isPointInPolygon(point, polygon)) {
      return point;
    }
    attempts++;
  }
  
  // Fallback: return center of bounding box if we can't find a point
  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2
  };
}

// Color palette for warehouse polygons (matching Sidebar colors)
const WAREHOUSE_POLYGON_COLORS = [
  { stroke: '#10b981', fill: '#10b981' }, // emerald
  { stroke: '#f59e0b', fill: '#f59e0b' }, // amber
  { stroke: '#8b5cf6', fill: '#8b5cf6' }, // violet
  { stroke: '#f43f5e', fill: '#f43f5e' }, // rose
  { stroke: '#06b6d4', fill: '#06b6d4' }, // cyan
  { stroke: '#f97316', fill: '#f97316' }, // orange
  { stroke: '#14b8a6', fill: '#14b8a6' }, // teal
  { stroke: '#ec4899', fill: '#ec4899' }, // pink
  { stroke: '#6366f1', fill: '#6366f1' }, // indigo
  { stroke: '#84cc16', fill: '#84cc16' }, // lime
];
const MAIN_WAREHOUSE_POLYGON_COLOR = { stroke: '#eab308', fill: '#eab308' }; // yellow/gold

export default function GoogleMapView({ center, zoom = 8, assets = [], zones = [], warehouses = [], selectedAssetId = null, selectedZoneId = null, onAssetClick, buildMode = false, buildPoints = [], onMapClick, onBuildPointDelete = null, selectedPointIndex = null, onPointSelect = null, mapTypeId = 'roadmap', blueprintEditMode = false, onBlueprintUpdate, currentBlueprint = null, currentWarehouse = null, gpsEditMode = false, onBuildPointUpdate = null, editingWarehouseId = null, onWarehouseClick = null, warehouseNavTarget = null, tripRoute = null, tripStartLocation = null, tripEndLocation = null }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const overlaysRef = useRef([]);
  const zonesRef = useRef([]);
  const buildOverlayRef = useRef({ markers: [], polyline: null });
  const searchInputRef = useRef(null);
  const editMarkersRef = useRef([]);
  const tripOverlaysRef = useRef({ polyline: null, startMarker: null, endMarker: null });

  // Cache for deterministic asset positions - stable across renders
  // Only recalculates when asset ID + zone polygon actually changes
  const assetPositionsCacheRef = useRef({});
  
  // Create stable keys to prevent unnecessary re-renders when data content is same
  // This ensures we only re-render overlays when asset IDs or zone IDs actually change
  const assetsKey = useMemo(() => assets.map(a => a.id).sort().join(','), [assets]);
  const zonesKey = useMemo(() => zones.map(z => z.id).sort().join(','), [zones]);
  
  // Store zones and assets data in refs so we can access them without adding to dependencies
  const zonesDataRef = useRef(zones);
  const assetsDataRef = useRef(assets);
  useEffect(() => {
    zonesDataRef.current = zones;
  }, [zones]);
  useEffect(() => {
    assetsDataRef.current = assets;
  }, [assets]);
  
  // Get deterministic position for an asset (cached)
  const getAssetPosition = useCallback((asset) => {
    if (!asset.zoneId || !ZONES_WITH_RANDOM_SCATTER.includes(asset.zoneId)) {
      return null;
    }
    
    const currentZones = zonesDataRef.current;
    const zone = currentZones.find(z => z.id === asset.zoneId);
    if (!zone || !zone.gps || !zone.gps.polygon) {
      return null;
    }
    
    // Create cache key from asset ID (polygon is assumed stable for same zone)
    const cacheKey = asset.id;
    
    // Return cached position if available
    if (assetPositionsCacheRef.current[cacheKey]) {
      return assetPositionsCacheRef.current[cacheKey];
    }
    
    // Calculate and cache new position
    const position = getDeterministicPointInPolygon(zone.gps.polygon, asset.id);
    assetPositionsCacheRef.current[cacheKey] = position;
    return position;
  }, []);

  useEffect(() => {
    // TODO: Replace with your actual Google Maps API key for testing
    const apiKey = 'AIzaSyAlezH6LRF4mHBxfAtQj-V-ymBtCFRF3FA';
    let mapInstance = null;
    let mounted = true;

    loadGoogleMaps(apiKey)
      .then(() => {
        if (!containerRef.current || !mounted) return;
        // Get current center and zoom props at the time of map creation
        const initialCenter = center;
        const initialZoom = zoom;
        mapInstance = new window.google.maps.Map(containerRef.current, {
          center: initialCenter,
          zoom: initialZoom,
          mapTypeId: mapTypeId, // Use prop
          mapTypeControl: false, // Disabled - using custom buttons instead
          streetViewControl: false,
          fullscreenControl: false,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.text',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.school',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.medical',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.place_of_worship',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.government',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.sports_complex',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });
        
        // Add click listener for build mode
        mapInstance.addListener('click', (e) => {
          if (onMapClickRef.current) {
            onMapClickRef.current({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          }
        });

        mapRef.current = mapInstance;
        setIsReady(true);
      })
      .catch(() => {
        if (mounted) setIsReady(false);
      });

    return () => {
      mounted = false;
      // Only clean up on unmount, not on center/zoom changes
      overlaysRef.current.forEach(o => { try { o.setMap(null); } catch(_){} });
      overlaysRef.current = [];
      zonesRef.current.forEach(z => { try { z.setMap(null); } catch(_){} });
      zonesRef.current = [];
      if (mapRef.current) mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount/unmount (center/zoom/mapTypeId are handled in separate useEffects)

  // Keep a ref to the latest onMapClick handler to avoid re-binding the listener
  const onMapClickRef = useRef(onMapClick);
  useEffect(() => {
    onMapClickRef.current = buildMode ? onMapClick : null;
  }, [onMapClick, buildMode]);

  // Keep a ref to onAssetClick to avoid re-creating overlays when callback changes
  const onAssetClickRef = useRef(onAssetClick);
  useEffect(() => {
    onAssetClickRef.current = onAssetClick;
  }, [onAssetClick]);

  // Handle warehouse navigation from sidebar clicks
  useEffect(() => {
    if (!mapRef.current || !isReady || !warehouseNavTarget?.center) return;
    
    const { lat, lng } = warehouseNavTarget.center;
    const latLng = new window.google.maps.LatLng(lat, lng);
    mapRef.current.panTo(latLng);
    mapRef.current.setZoom(17);
  }, [warehouseNavTarget, isReady]);

  useEffect(() => {
    if (mapRef.current && isReady) {
      const latLng = new window.google.maps.LatLng(center.lat, center.lng);
      mapRef.current.panTo(latLng);
      mapRef.current.setZoom(zoom);
    }
  }, [center, zoom, isReady]);

  // Update Map Type
  useEffect(() => {
    if (mapRef.current && isReady) {
      mapRef.current.setMapTypeId(mapTypeId);
    }
  }, [mapTypeId, isReady]);

  // Initialize Search Box
  useEffect(() => {
    if (!mapRef.current || !window.google || !searchInputRef.current || !isReady) return;

    const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current);
    autocomplete.bindTo('bounds', mapRef.current);

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) {
        return;
      }

      if (place.geometry.viewport) {
        mapRef.current.fitBounds(place.geometry.viewport);
      } else {
        mapRef.current.setCenter(place.geometry.location);
        mapRef.current.setZoom(18); // Zoom in close for building outline
      }
    });

    return () => {
      if (window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [isReady]);

  // Render build mode elements (markers and polyline)
  useEffect(() => {
    if (!mapRef.current || !isReady) return;

    // Clear previous build elements
    if (buildOverlayRef.current.polyline) {
      buildOverlayRef.current.polyline.setMap(null);
      buildOverlayRef.current.polyline = null;
    }
    buildOverlayRef.current.markers.forEach(m => m.setMap(null));
    buildOverlayRef.current.markers = [];

    if (!buildMode || buildPoints.length === 0) return;

    // All markers are now draggable
    // Edit mode uses amber color, create mode uses blue
    const isEditMode = gpsEditMode;
    const baseColor = isEditMode ? '#f59e0b' : '#2563eb'; // amber for edit, blue for create
    const selectedColor = '#ef4444'; // red for selected

    // Draw markers for each point
    buildPoints.forEach((pt, index) => {
      const isSelected = selectedPointIndex === index;
      const markerColor = isSelected ? selectedColor : baseColor;
      
      const marker = new window.google.maps.Marker({
        position: pt,
        map: mapRef.current,
        draggable: true, // All markers are draggable now
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: isSelected ? 10 : 8, // Larger for selected
          fillColor: markerColor,
          fillOpacity: 1,
          strokeWeight: isSelected ? 3 : 2,
          strokeColor: isSelected ? '#ffffff' : '#ffffff'
        },
        zIndex: isSelected ? 1002 : 1000,
        title: `Point ${index + 1} - Click to select, drag to move`
      });

      // Click to select/deselect
      marker.addListener('click', () => {
        if (onPointSelect) {
          onPointSelect(selectedPointIndex === index ? null : index);
        }
      });

      // Drag for real-time movement (no history yet)
      marker.addListener('drag', () => {
        const newPos = marker.getPosition();
        if (newPos && onBuildPointUpdate) {
          onBuildPointUpdate(index, { lat: newPos.lat(), lng: newPos.lng() }, false);
        }
      });

      // Drag end to save to history
      marker.addListener('dragend', () => {
        const newPos = marker.getPosition();
        if (newPos && onBuildPointUpdate) {
          onBuildPointUpdate(index, { lat: newPos.lat(), lng: newPos.lng() }, true);
        }
      });

      buildOverlayRef.current.markers.push(marker);
    });

    // Draw polyline connecting points
    // If we have > 2 points, close the loop visually (optional, but good for polygons)
    const path = [...buildPoints];
    if (path.length > 2) {
      path.push(path[0]); // Close the loop
    }

    const polyline = new window.google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: baseColor,
      strokeOpacity: 0.8,
      strokeWeight: 3,
      map: mapRef.current,
      zIndex: 999
    });
    buildOverlayRef.current.polyline = polyline;

  }, [buildPoints, buildMode, isReady, gpsEditMode, onBuildPointUpdate, selectedPointIndex, onPointSelect]);

  // Render warehouse and zone polygons
  useEffect(() => {
    if (!mapRef.current || !window.google || !window.google.maps || !isReady) return;
    // clear previous polygons
    zonesRef.current.forEach(z => { try { z.setMap(null); } catch(_){} });
    zonesRef.current = [];

    // First, render warehouses (warehouses have GPS polygons)
    const warehousesToRender = Array.isArray(warehouses) ? warehouses : Object.values(warehouses || {});
    
    // Separate customer and partner warehouses for color assignment
    const customerWarehouses = warehousesToRender.filter(wh => wh.org_type === 'Customer');
    const partnerWarehouses = warehousesToRender.filter(wh => wh.org_type !== 'Customer');
    
    warehousesToRender.forEach((warehouse) => {
      if (!warehouse.gps || !warehouse.gps.polygon) return;

      const polygonCoords = warehouse.gps.polygon.map(coord => ({
        lat: coord.lat,
        lng: coord.lng
      }));

      // Determine color based on org_type
      let warehouseColor;
      if (warehouse.org_type === 'Customer') {
        warehouseColor = MAIN_WAREHOUSE_POLYGON_COLOR;
      } else {
        // Get index among partner warehouses for consistent coloring
        const partnerIndex = partnerWarehouses.findIndex(wh => wh.id === warehouse.id);
        warehouseColor = WAREHOUSE_POLYGON_COLORS[partnerIndex % WAREHOUSE_POLYGON_COLORS.length];
      }

      // Warehouse polygon styling with unique colors
      const isEditing = blueprintEditMode && currentWarehouse?.id === warehouse.id;
      const polygon = new window.google.maps.Polygon({
        paths: polygonCoords,
        strokeColor: isEditing ? '#ef4444' : warehouseColor.stroke,
        strokeOpacity: 0.9,
        strokeWeight: isEditing ? 4 : 3,
        fillColor: warehouseColor.fill,
        fillOpacity: 0.2,
        editable: isEditing,
        draggable: false,
        clickable: true,
      });
      
      // Add click handler to pan/zoom to warehouse
      polygon.addListener('click', () => {
        if (onWarehouseClick && !blueprintEditMode) {
          // Calculate center of polygon
          const bounds = new window.google.maps.LatLngBounds();
          polygonCoords.forEach(coord => bounds.extend(coord));
          const center = bounds.getCenter();
          
          onWarehouseClick(warehouse.id, { lat: center.lat(), lng: center.lng() });
          
          // Pan and zoom to warehouse
          if (mapRef.current) {
            mapRef.current.panTo(center);
            mapRef.current.setZoom(17);
          }
        }
      });

      // Add vertex markers for editing
      if (blueprintEditMode && currentWarehouse?.id === warehouse.id) {
        polygonCoords.forEach((coord, idx) => {
          const marker = new window.google.maps.Marker({
            position: coord,
            map: mapRef.current,
            draggable: true,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#3b82f6',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
            zIndex: 1000,
          });

          // Handle marker drag
          marker.addListener('dragend', () => {
            const newPath = polygon.getPath();
            const newCoords = [];
            for (let i = 0; i < newPath.getLength(); i++) {
              const latLng = newPath.getAt(i);
              newCoords.push({ lat: latLng.lat(), lng: latLng.lng() });
            }
            
            // Convert GPS to blueprint outlinePoints
            if (onBlueprintUpdate && currentBlueprint) {
              const origin = { lat: Math.max(...newCoords.map(c => c.lat)), lng: Math.min(...newCoords.map(c => c.lng)) };
              const METERS_PER_DEG_LAT = 111320;
              const METERS_PER_DEG_LNG = 111320 * Math.cos(origin.lat * Math.PI / 180);
              
              const outlinePoints = newCoords.map(c => ({
                x: Math.round((c.lng - origin.lng) * METERS_PER_DEG_LNG),
                y: Math.round((origin.lat - c.lat) * METERS_PER_DEG_LAT)
              }));
              
              onBlueprintUpdate({ ...currentBlueprint, outlinePoints });
            }
          });

          // Right-click to delete
          marker.addListener('rightclick', (e) => {
            e.stop();
            if (polygonCoords.length <= 3) {
              alert("Cannot delete corner. Blueprint must have at least 3 points.");
              return;
            }
            
            const newCoords = polygonCoords.filter((_, i) => i !== idx);
            const newPath = newCoords.map(c => new window.google.maps.LatLng(c.lat, c.lng));
            polygon.setPath(newPath);
            marker.setMap(null);
            
            // Update blueprint
            if (onBlueprintUpdate && currentBlueprint) {
              const origin = { lat: Math.max(...newCoords.map(c => c.lat)), lng: Math.min(...newCoords.map(c => c.lng)) };
              const METERS_PER_DEG_LAT = 111320;
              const METERS_PER_DEG_LNG = 111320 * Math.cos(origin.lat * Math.PI / 180);
              
              const outlinePoints = newCoords.map(c => ({
                x: Math.round((c.lng - origin.lng) * METERS_PER_DEG_LNG),
                y: Math.round((origin.lat - c.lat) * METERS_PER_DEG_LAT)
              }));
              
              onBlueprintUpdate({ ...currentBlueprint, outlinePoints });
            }
          });

          editMarkersRef.current.push(marker);
        });
      }

      polygon.setMap(mapRef.current);
      zonesRef.current.push(polygon);

      // Add warehouse label
      const bounds = new window.google.maps.LatLngBounds();
      polygonCoords.forEach(coord => bounds.extend(coord));
      const center = bounds.getCenter();

      const labelOverlay = new window.google.maps.OverlayView();
      labelOverlay.onAdd = function() {
        const labelDiv = document.createElement('div');
        labelDiv.style.position = 'absolute';
        labelDiv.style.fontWeight = 'bold';
        labelDiv.style.fontSize = '18px';
        labelDiv.style.color = '#3b82f6';
        labelDiv.style.pointerEvents = 'none';
        labelDiv.style.textShadow = '1px 1px 2px rgba(255, 255, 255, 0.9), -1px -1px 2px rgba(255, 255, 255, 0.9)';
        labelDiv.textContent = warehouse.display_name || warehouse.name;
        
        this.getPanes().overlayLayer.appendChild(labelDiv);
        this.labelDiv = labelDiv;
      };
      
      labelOverlay.draw = function() {
        const projection = this.getProjection();
        const position = projection.fromLatLngToDivPixel(center);
        if (this.labelDiv && position) {
          this.labelDiv.style.left = (position.x - this.labelDiv.offsetWidth / 2) + 'px';
          this.labelDiv.style.top = (position.y - this.labelDiv.offsetHeight / 2) + 'px';
        }
      };
      
      labelOverlay.onRemove = function() {
        if (this.labelDiv && this.labelDiv.parentNode) {
          this.labelDiv.parentNode.removeChild(this.labelDiv);
        }
      };
      
      labelOverlay.setMap(mapRef.current);
      zonesRef.current.push(labelOverlay);
    });

    // Zones are not rendered in Google Map view - only warehouses are shown

    return () => {
      zonesRef.current.forEach(z => { try { z.setMap(null); } catch(_){} });
      zonesRef.current = [];
      editMarkersRef.current.forEach(m => { try { m.setMap(null); } catch(_){} });
      editMarkersRef.current = [];
    };
  }, [zones, warehouses, selectedZoneId, isReady, blueprintEditMode, currentWarehouse, currentBlueprint, onBlueprintUpdate]);

  // Render asset pings as DOM overlays
  useEffect(() => {
    if (!mapRef.current || !window.google || !window.google.maps || !isReady) return;
    // clear previous overlays
    overlaysRef.current.forEach(o => { try { o.setMap(null); } catch(_){} });
    overlaysRef.current = [];

    // Get image URL for an asset - prefer the image from database (GCS URL)
    // Fall back to type-based lookup only if no image is provided
    const getAssetImage = (asset) => {
      // If asset has an image URL from the database (GCS), use it directly
      if (asset.image) {
        // If it's already a full URL, use as-is
        if (asset.image.startsWith('http')) {
          return asset.image;
        }
        // Otherwise it's a filename, get the proper URL
        return getAssetImageUrl(asset.image);
      }
      
      // Fallback: use type-based lookup with URL-safe filenames
      const key = (asset.assetType || asset.type || '').toLowerCase();
      
      // Handle vehicles - use a truck/car icon
      if (key === 'vehicle') {
        // Return a simple SVG data URL for a vehicle icon
        return 'data:image/svg+xml;base64,' + btoa(`
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="${asset.live ? '#22c55e' : '#3b82f6'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"></path>
            <circle cx="6.5" cy="16.5" r="2.5"></circle>
            <circle cx="16.5" cy="16.5" r="2.5"></circle>
          </svg>
        `);
      }
      
      // Map common types to their image files
      if (key === 'racks') return getAssetImageUrl('Racks.png');
      if (key === 'racks (large)') return getAssetImageUrl('Racks_Large.png');
      if (key === 'shipping rack') return getAssetImageUrl('Shipping Rack.png');
      if (key === 'material handling (rack)') return getAssetImageUrl('Material Handling (Rack).png');
      if (key === 'metal storage (rack)') return getAssetImageUrl('Metal Storage (Rack).png');
      if (key === 'warehouse (rack)') return getAssetImageUrl('Warehouse (Rack).png');
      if (key === 'automotive parts (rack)') return getAssetImageUrl('Automotive Parts (Rack).png');
      if (key === 'dunnage (rack)') return getAssetImageUrl('Dunnage (Rack).png');
      if (key === 'steel rack (dunnage)') return getAssetImageUrl('Steel Rack (Dunnage).png');
      if (key === 'crates') return getAssetImageUrl('Crates.png');
      if (key === 'crates (large)') return getAssetImageUrl('Crates_Large.png');
      if (key === 'crates (small)') return getAssetImageUrl('Crates_Small.png');
      if (key === 'totes') return getAssetImageUrl('Totes_Medium.png');
      if (key === 'totes (large)') return getAssetImageUrl('Totes_Large.png');
      if (key === 'totes (medium)') return getAssetImageUrl('Totes_Medium.png');
      if (key === 'totes (small)') return getAssetImageUrl('Totes_Small.png');
      if (key === 'bins') return getAssetImageUrl('Bins_Medium.png');
      if (key === 'bins (large)') return getAssetImageUrl('Bins_Large.png');
      if (key === 'bins (medium)') return getAssetImageUrl('Bins_Medium.png');
      if (key === 'bins (small)') return getAssetImageUrl('Bins_Small.png');
      if (key === 'returnable (bin)') return getAssetImageUrl('Returnable (Bin).png');
      if (key === 'industrial scrap (bin)') return getAssetImageUrl('Industrial Scrap (Bin).png');
      if (key === 'metal scrap large (bin)') return getAssetImageUrl('Metal Scrap Large (Bin).png');
      if (key === 'metal scrap small (bin)') return getAssetImageUrl('Metal Scrap Small (Bin).png');
      if (key === 'metal stacking (bin)') return getAssetImageUrl('Metal Stacking (Bin).png');
      if (key === 'pallets') return getAssetImageUrl('Pallets.png');
      if (key === 'automotive packaging (dunnage)') return getAssetImageUrl('Automotive Packaging (Dunnage).png');
      if (key === 'custom foam (dunnage)') return getAssetImageUrl('Custom Foam (Dunnage).png');
      if (key === 'plastic (dunnage)') return getAssetImageUrl('Plastic (Dunnage).png');
      if (key === 'returnable package (dunnage)') return getAssetImageUrl('Returnable Package (Dunnage).png');
      return getAssetImageUrl('Pallets.png');
    };

    // Use ref to access assets without causing dependency re-renders
    const currentAssets = assetsDataRef.current;
    
    currentAssets.forEach((asset) => {
      const el = document.createElement('div');
      el.className = 'gm-ping' + 
        (selectedAssetId && asset.id === selectedAssetId ? ' selected' : '') +
        (asset.type === 'Vehicle' && asset.live ? ' vehicle-live' : '') +
        (asset.type === 'Vehicle' ? ' vehicle' : '');
      
      // Disable pointer events and dim assets when no zone is selected
      // But always show vehicles at full opacity
      if (!selectedZoneId && asset.type !== 'Vehicle') {
        el.style.opacity = '0.4';
      }
      
      const icon = document.createElement('img');
      icon.className = 'icon';
      icon.alt = asset.assetType || asset.type || 'asset';
      
      // Use the asset's image from database (GCS URL) if available
      const imageSrc = getAssetImage(asset);
      
      // Check if this image has already failed - use fallback immediately
      if (failedImageCache.has(imageSrc)) {
        icon.src = getAssetImageUrl('Pallets.png'); // Use fallback
      } else {
        icon.src = imageSrc;
        icon.onerror = () => {
          // Only log once per unique image URL
          if (!failedImageCache.has(imageSrc)) {
            console.warn('Failed to load image, using fallback:', imageSrc);
            failedImageCache.add(imageSrc);
          }
          // Use fallback image
          icon.src = getAssetImageUrl('Pallets.png');
        };
      }
      
      const dot = document.createElement('div');
      dot.className = 'dot';
      const ring = document.createElement('div');
      ring.className = 'ring';
      
      el.appendChild(icon);
      el.appendChild(dot);
      el.appendChild(ring);

      const overlay = new window.google.maps.OverlayView();
      
      // Use deterministic position within polygon for zones A, B, C; otherwise use asset's lat/lng
      const cachedPos = getAssetPosition(asset);
      const assetLat = cachedPos ? cachedPos.lat : asset.lat;
      const assetLng = cachedPos ? cachedPos.lng : asset.lng;
      const assetId = asset.id;
      
      overlay.onAdd = function() {
        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(el);
        
        // Only enable clicking if a zone is selected
        if (selectedZoneId) {
          el.style.pointerEvents = 'auto';
          el.style.cursor = 'pointer';
          
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            const latLng = new window.google.maps.LatLng(assetLat, assetLng);
            if (mapRef.current) {
              mapRef.current.panTo(latLng);
              mapRef.current.setZoom(18);
            }
            if (onAssetClickRef.current) {
              onAssetClickRef.current(assetId);
            }
          });
        } else {
          el.style.pointerEvents = 'none';
          el.style.cursor = 'default';
        }
      };
      
      overlay.draw = function() {
        const proj = this.getProjection();
        const pos = proj.fromLatLngToDivPixel(new window.google.maps.LatLng(assetLat, assetLng));
        if (!pos) return;
        el.style.left = pos.x + 'px';
        el.style.top = pos.y + 'px';
      };
      
      overlay.onRemove = function() {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      };
      
      overlay.setMap(mapRef.current);
      overlaysRef.current.push(overlay);
    });

    return () => {
      overlaysRef.current.forEach(o => { try { o.setMap(null); } catch(_){} });
      overlaysRef.current = [];
    };
    // Note: Using stable keys (assetsKey, zonesKey) instead of arrays to prevent
    // unnecessary re-renders when data content is same but reference changes.
    // onAssetClick is accessed via ref. getAssetPosition uses ref-based cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsKey, zonesKey, selectedAssetId, selectedZoneId, isReady, getAssetPosition]);

  // Render trip route (polyline and start/end markers)
  useEffect(() => {
    if (!mapRef.current || !window.google || !isReady) return;

    // Clear existing trip overlays
    if (tripOverlaysRef.current.polyline) {
      tripOverlaysRef.current.polyline.setMap(null);
      tripOverlaysRef.current.polyline = null;
    }
    if (tripOverlaysRef.current.startMarker) {
      tripOverlaysRef.current.startMarker.setMap(null);
      tripOverlaysRef.current.startMarker = null;
    }
    if (tripOverlaysRef.current.endMarker) {
      tripOverlaysRef.current.endMarker.setMap(null);
      tripOverlaysRef.current.endMarker = null;
    }

    // If no trip route, nothing to render
    if (!tripRoute || tripRoute.length === 0) return;

    // Create polyline path
    const path = tripRoute.map(point => ({
      lat: parseFloat(point.lat),
      lng: parseFloat(point.lon)
    }));

    // Create polyline
    const polyline = new window.google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: '#3B82F6', // Blue
      strokeOpacity: 0.8,
      strokeWeight: 4,
      map: mapRef.current
    });
    tripOverlaysRef.current.polyline = polyline;

    // Fit bounds to show entire trip route
    if (path.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Add all route points to bounds
      path.forEach(point => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });
      
      // Add start and end markers to bounds if available
      if (tripStartLocation) {
        bounds.extend(new window.google.maps.LatLng(
          parseFloat(tripStartLocation.lat),
          parseFloat(tripStartLocation.lng)
        ));
      }
      if (tripEndLocation) {
        bounds.extend(new window.google.maps.LatLng(
          parseFloat(tripEndLocation.lat),
          parseFloat(tripEndLocation.lng)
        ));
      }
      
      // Fit bounds with padding
      mapRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
    }

    // Create start marker (green)
    if (tripStartLocation) {
      const startMarker = new window.google.maps.Marker({
        position: { 
          lat: parseFloat(tripStartLocation.lat), 
          lng: parseFloat(tripStartLocation.lng) 
        },
        map: mapRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#10B981', // Green
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        },
        title: 'Trip Start',
        zIndex: 1000
      });
      tripOverlaysRef.current.startMarker = startMarker;
    }

    // Create end marker (red)
    if (tripEndLocation) {
      const endMarker = new window.google.maps.Marker({
        position: { 
          lat: parseFloat(tripEndLocation.lat), 
          lng: parseFloat(tripEndLocation.lng) 
        },
        map: mapRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#EF4444', // Red
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        },
        title: 'Trip End',
        zIndex: 1000
      });
      tripOverlaysRef.current.endMarker = endMarker;
    }

    return () => {
      if (tripOverlaysRef.current.polyline) {
        tripOverlaysRef.current.polyline.setMap(null);
      }
      if (tripOverlaysRef.current.startMarker) {
        tripOverlaysRef.current.startMarker.setMap(null);
      }
      if (tripOverlaysRef.current.endMarker) {
        tripOverlaysRef.current.endMarker.setMap(null);
      }
    };
  }, [tripRoute, tripStartLocation, tripEndLocation, isReady]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" aria-label={isReady ? 'Google Map' : 'Loading Google Map...'} />
      
      {/* Search Box Overlay */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 w-80 sm:w-96 shadow-xl">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search address to outline..."
          className="w-full px-4 py-2.5 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-gray-100 placeholder-gray-400 text-sm"
        />
      </div>
    </div>
  );
}